// Pure helpers for the crm-chat-api visual tools (render_visual,
// generate_image), kept free of React so messageSegments.ts and node tests
// can import them.

import type { ToolPart } from './ApprovalActions';
import type { ArtifactSummary } from './artifactFiles';

// Mirror RENDER_VISUAL_TOOL_NAME / GENERATE_IMAGE_TOOL_NAME in
// crm-chat-api/tools/visuals.ts.
export const RENDER_VISUAL_TOOL_NAME = 'render_visual';
export const GENERATE_IMAGE_TOOL_NAME = 'generate_image';

export const isVisualToolName = (toolName: string): boolean =>
  toolName === RENDER_VISUAL_TOOL_NAME || toolName === GENERATE_IMAGE_TOOL_NAME;

// `VisualToolOutput` in the chat visuals contract. Parsed defensively: a
// persisted output can come from an older or newer server.
export type VisualImage = {
  imageId: string;
  url: string | null;
  downloadUrl: string | null;
  width: number | null;
  height: number | null;
  mediaType: string;
};

export type VisualToolOutput = {
  kind: 'render' | 'generate' | null;
  title: string | null;
  images: Array<VisualImage>;
  modelId: string | null;
  // The copy crm-chat-api saved in the user's Files, or why it did not; null
  // when the server saved nothing (store off, older output).
  savedToFiles: SavedToFiles | null;
  error: string | null;
};

export type SavedToFiles =
  | { artifactId: string; name: string }
  | { error: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value != null && !Array.isArray(value);

const str = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const dimension = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.round(value)
    : null;

// sha256 hex, as minted by crm-chat-api storeGeneratedImage.
const IMAGE_ID_PATTERN = /^[0-9a-f]{64}$/i;

// Only GCS V4 signed links (lib/attachments.ts signGeneratedImage) are ever
// loaded or opened; anything else is treated as missing (expired chip).
const SIGNED_URL_PREFIX = 'https://storage.googleapis.com/';

const signedUrl = (value: unknown): string | null => {
  const url = str(value);
  return url != null && url.startsWith(SIGNED_URL_PREFIX) ? url : null;
};

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const parseSavedToFiles = (value: unknown): SavedToFiles | null => {
  if (!isRecord(value)) {
    return null;
  }
  const artifactId = str(value.artifactId);
  const name = str(value.name);
  if (artifactId != null && UUID_PATTERN.test(artifactId) && name != null) {
    return { artifactId, name };
  }
  const error = str(value.error);
  return error != null ? { error } : null;
};

const parseImage = (value: unknown): VisualImage | null => {
  if (!isRecord(value)) {
    return null;
  }
  const imageId = str(value.imageId);
  if (imageId == null || !IMAGE_ID_PATTERN.test(imageId)) {
    return null;
  }
  return {
    imageId,
    url: signedUrl(value.url),
    downloadUrl: signedUrl(value.downloadUrl),
    width: dimension(value.width),
    height: dimension(value.height),
    mediaType: str(value.mediaType) ?? 'image/png',
  };
};

export const parseVisualToolOutput = (output: unknown): VisualToolOutput => {
  const record = isRecord(output) ? output : {};
  const kind =
    record.kind === 'render' || record.kind === 'generate' ? record.kind : null;
  const images = Array.isArray(record.images)
    ? record.images
        .map(parseImage)
        .filter((image): image is VisualImage => image != null)
    : [];
  return {
    kind,
    title: str(record.title),
    images,
    modelId: str(record.modelId),
    savedToFiles: parseSavedToFiles(record.savedToFiles),
    // A plain-string output is a server-side refusal (e.g. quota) relayed
    // verbatim; keep it visible as the error text.
    error:
      str(record.error) ??
      (typeof output === 'string' && images.length === 0 ? str(output) : null),
  };
};

// A successful call that produced no image counts as failed: the contract sets
// `error` then.
export type VisualToolPhase = 'running' | 'done' | 'failed';

export const visualToolPhaseOf = (part: ToolPart): VisualToolPhase => {
  if (
    part.state === 'output-error' ||
    part.state === 'output-denied' ||
    part.errorText != null
  ) {
    return 'failed';
  }
  if (part.state === 'output-available') {
    const output = parseVisualToolOutput(part.output);
    return output.error == null && output.images.length > 0 ? 'done' : 'failed';
  }
  return 'running';
};

// The visual's copy in the user's Files, shaped for the shared file preview
// and download (useChatFile), or null when none was saved. Only the id, name
// and type are known here; they are all the preview and download use.
export const savedVisualArtifactOf = (
  output: VisualToolOutput,
): ArtifactSummary | null => {
  const saved = output.savedToFiles;
  const image = output.images[0];
  if (saved == null || !('artifactId' in saved) || image == null) {
    return null;
  }
  return {
    id: saved.artifactId,
    name: saved.name,
    mimeType: image.mediaType,
    sizeBytes: 0,
    source: 'visual',
    chatId: null,
    createdAt: '',
  };
};

// generate_image's gateway-billed USD (crm-chat-api VisualToolOutput.costUsd);
// null when unreported, for render_visual and for older outputs.
export const visualCostUsdOf = (output: unknown): number | null => {
  const cost = isRecord(output) ? output.costUsd : null;
  return typeof cost === 'number' && Number.isFinite(cost) && cost >= 0
    ? cost
    : null;
};

export const visualTitleOf = (
  input: unknown,
  output: VisualToolOutput,
): string | null => output.title ?? (isRecord(input) ? str(input.title) : null);

// downloadUrl carries `Content-Disposition: attachment`; the plain read URL
// still opens the image when it is missing.
export const downloadUrlOf = (image: VisualImage): string | null =>
  image.downloadUrl ?? image.url;

export const formatImageSize = (image: VisualImage): string | null =>
  image.width != null && image.height != null
    ? `${image.width}×${image.height}`
    : null;

// Thumbnail box that keeps the image's aspect ratio inside max bounds, so a
// story (1080×1920) is not shown as tall as the chat.
export const thumbnailSizeOf = (
  image: VisualImage,
  max: { width: number; height: number },
): { width: number; height: number } => {
  if (image.width == null || image.height == null) {
    return { width: max.width, height: max.width };
  }
  const scale = Math.min(1, max.width / image.width, max.height / image.height);
  return {
    width: Math.round(image.width * scale),
    height: Math.round(image.height * scale),
  };
};

const EXTENSIONS: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
};

// Same name crm-chat-api gives a generated image saved to a property.
export const visualFilenameOf = (image: VisualImage): string =>
  `visual-${image.imageId.slice(0, 8)}.${EXTENSIONS[image.mediaType] ?? 'png'}`;

// crm-chat-api `GET /chat/:chatId/visuals/:imageId`: the image bytes behind
// the chat's own auth, so Copy needs no CORS on the signed GCS URL.
export const visualBytesUrlOf = (
  chatApiBase: string,
  chatId: string,
  imageId: string,
): string =>
  `${chatApiBase}/${encodeURIComponent(chatId)}/visuals/${encodeURIComponent(
    imageId,
  )}`;

// Fetches the image for Copy. A non-2xx keeps its status and body in the
// error so the card can show it verbatim.
export const fetchVisualBytes = async ({
  url,
  headers,
  fetchImpl,
}: {
  url: string;
  headers: Record<string, string>;
  fetchImpl: typeof fetch;
}): Promise<Blob> => {
  const response = await fetchImpl(url, { headers });
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(
      `GET ${url} → HTTP ${response.status} ${response.statusText}${
        body.length > 0 ? `\n${body.slice(0, 1000)}` : ''
      }`,
    );
  }
  return response.blob();
};

export type ClipboardEnv = {
  ClipboardItem?: unknown;
  navigator?: { clipboard?: { write?: unknown } };
};

export const canCopyImages = (
  env: ClipboardEnv = globalThis as ClipboardEnv,
): boolean =>
  typeof env.ClipboardItem === 'function' &&
  typeof env.navigator?.clipboard?.write === 'function';

export const describeError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.name !== 'Error' && !error.message.startsWith(error.name)
      ? `${error.name}: ${error.message}`
      : error.message;
  }
  return typeof error === 'string'
    ? error
    : JSON.stringify(error) ?? String(error);
};
